import {
  plusLightIcon,
  editIcon,
  deleteIcon,
  documentPreviewIcon,
  closeIcon,
} from "assets/images";
import NoRecords from "components/NoRecords";
import {
  TableCell,
  Box,
  SelectChangeEvent,
  Typography,
  Button,
  Grid,
  Card,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  InputAdornment,
  Fade,
  Tooltip,
} from "@mui/material";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_NO,
  EDIT_TOOLTIP_TITLE,
  DELETE_TOOLTIP_TITLE,
  CLEAR_TOOLTIP_TITLE,
  DOCUMENT_TYPE,
} from "utils/constants/constant";
import { AppPages } from "utils/enums/app-pages";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
  getPagePayloadValues,
  getPagePermissions,
  allowSearch,
  showLoader,
  onTextFieldKeyPress,
} from "utils/helper";
import {
  IDocumentContainerState,
  IDocumentContainerDispatch,
  IEnhancedTableProps,
  IDocument,
  IDocumentListMeta,
  IAddEditDocument,
  IDocumentPagePayloadValues,
  IDocumentPreview,
} from "utils/interfaces/document";
import projectTheme from "app.theme";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { IRoleModulePermission } from "store/role/types";
import {
  AddEditDocumentSuccessPayload,
  DeleteDocumentSuccessPayload,
  DocumentListSuccessPayload,
} from "store/document/types";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import {
  DOCUMENT_ADD_SUCCESS_MSG,
  DOCUMENT_DELETE_MODAL_MSG,
  DOCUMENT_DELETE_SUCCESS_MSG,
  DOCUMENT_EDIT_SUCCESS_MSG,
} from "utils/constants/Messages";
import { toast } from "react-toastify";
import CustomPagination from "components/customPagination";
import AddEditDocument from "./AddEditDocument";

const EnhancedTableHead = (props: IEnhancedTableProps) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof IDocument) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};

export type DocumentProps = IDocumentContainerState &
  IDocumentContainerDispatch;

const Documents: React.FC<DocumentProps> = (props) => {
  const [documentList, setDocumentList] = useState<IDocument[]>();
  const [documentListMeta, setDocumentListMeta] = useState<IDocumentListMeta>();
  const [page, setPage] = useState(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [order, setOrder] = useState<Order>(OrderByOptions.Asc);
  const [orderBy, setOrderBy] = useState<keyof IDocument>("name");
  const [searchText, setSearchText] = useState<string>("");
  const [pagePermission, setPagePermission] = useState<IRoleModulePermission>();
  const [filteredData, setFilteredData] = useState<IAddEditDocument>();
  const [appliedFilter, setAppliedFilter] = useState<boolean>(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState<number>();
  const [isEditDocument, setIsEditDocument] = useState<boolean>(false);
  const [isOpenDocumentModal, setIsOpenDocumentModal] =
    useState<boolean>(false);
  const [editDocumentData, setEditDocumentData] = useState<IDocument>();
  const [isOpenDocumentDeleteModal, setIsOpenDocumentDeleteModal] =
    useState<boolean>(false);
  const history = useHistory();
  const document = useSelector((state: any) => state?.auth.user);

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(1);
  };

  const onPageChange = (e: any, value: number) => {
    setPageNo(value);
  };

  const handleRequestSort = React.useCallback(
    (event: React.MouseEvent<unknown>, newOrderBy: keyof IDocument) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const onDocumentListSuccess = (response: DocumentListSuccessPayload) => {
    setDocumentList(response.documentList);
    setDocumentListMeta(response.documentListPagination);
    hideLoaderForBatchApiCall();
  };

  const getDocumentList = async (
    filterData?: IAddEditDocument,
    filtered?: boolean,
    pageNum?: number
  ) => {
    const { documentListRequest } = props;
    if (documentListRequest) {
      showLoaderForBatchApiCall();

      const values: IDocumentPagePayloadValues = getPagePayloadValues(
        orderBy,
        order,
        pageNo,
        page
      );
      values.searchName = filtered ? "" : searchText.trim();

      if (filterData) {
        setFilteredData(filterData);
        setAppliedFilter(true);

        if (pageNum) {
          values.pageNo = pageNum;
          setPageNo(pageNum);
        }
      } else {
        setFilteredData(undefined);
      }
      const payload = {
        values,
        callback: onDocumentListSuccess,
      };
      documentListRequest(payload);
    }
  };

  useEffect(() => {
    const permission = getPagePermissions(
      document?.permissions,
      AppPages.Documents
    );
    setPagePermission(permission);
    if (permission?.view) {
      if (filteredData && !appliedFilter) {
        getDocumentList(filteredData, true, 1);
      } else if (filteredData) {
        getDocumentList(filteredData);
      } else {
        getDocumentList();
      }
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageNo, order, orderBy]);

  const handleDocumentDeleteModal = (documentId: number) => {
    setDeleteDocumentId(documentId);
    setIsOpenDocumentDeleteModal(true);
  };

  const handleDocumentDeleteCloseModal = () => {
    setDeleteDocumentId(0);
    setIsOpenDocumentDeleteModal(false);
  };

  const handleDocumentSearch = (event: any) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
    getDocumentList(filteredData, true, 1);
  };

  const handleDocumentSearchKeyDown = (e: any) => {
    if (allowSearch(e)) {
      setPageNo(1);
      getDocumentList(filteredData);
    }
  };
  const onDeleteDocumentSuccess = (response: DeleteDocumentSuccessPayload) => {
    if (response.success) {
      toast.success(DOCUMENT_DELETE_SUCCESS_MSG);
      handleDocumentDeleteCloseModal();
      getDocumentList();
    }
  };

  const handleDeleteDocument = () => {
    const { deleteDocumentRequest } = props;

    if (deleteDocumentRequest) {
      showLoader();
      const payload = {
        values: {
          id: Number(deleteDocumentId),
          type: 0,
          courseOfferingID: 0,
        },
        callback: onDeleteDocumentSuccess,
      };

      deleteDocumentRequest(payload);
    }
  };

  const handleDocumentClose = () => {
    setIsOpenDocumentModal(false);
    setIsEditDocument(false);
    setEditDocumentData({
      name: "",
      description: "",
      id: 0,
      link: "",
      type: "",
      document: "",
      createdBy: 0,
      updatedBy: 0,
      updatedOn: "",
      createdOn: "",
    });
  };

  const onAddDocumentSuccess = (response: AddEditDocumentSuccessPayload) => {
    if (response.success) {
      toast.success(DOCUMENT_ADD_SUCCESS_MSG);
      handleDocumentClose();
      getDocumentList();
    } else if (response.message) {
      toast.warning(response.message);
    }
  };

  const onEditDocumentSuccess = (response: AddEditDocumentSuccessPayload) => {
    if (response.success) {
      toast.success(DOCUMENT_EDIT_SUCCESS_MSG);
      handleDocumentClose();
      getDocumentList();
    } else if (response.message) {
      toast.warning(response.message);
    }
  };

  const handleEditDocument = (formData: IAddEditDocument) => {
    const { editDocumentRequest } = props;
    if (editDocumentRequest) {
      showLoader();
      const payload = {
        values: {
          id: editDocumentData?.id,
          name: formData?.name.trim(),
          type: formData?.type?.trim(),
          link: formData?.link?.trim(),
          description: formData?.description?.trim(),
          document: formData?.document,
        },
        callback: onEditDocumentSuccess,
      };
      editDocumentRequest(payload);
    }
  };

  const handleAddDocument = (formData: IAddEditDocument) => {
    const { addDocumentRequest } = props;
    if (addDocumentRequest) {
      showLoader();
      const payload = {
        values: {
          name: formData?.name.trim(),
          type: formData?.type?.trim(),
          link: formData?.link?.trim(),
          description: formData?.description?.trim(),
          document: formData?.document,
        },
        callback: onAddDocumentSuccess,
      };
      addDocumentRequest(payload);
    }
  };

  const handleDocument = (isEdit: boolean, editData: IDocument | null) => {
    setIsEditDocument(isEdit);
    if (editData) {
      setEditDocumentData(editData);
    } else {
      setEditDocumentData(undefined);
    }
    setIsOpenDocumentModal(true);
  };

  const onDocumentPreviewSuccess = (response: IDocumentPreview) => {
    if (response && response.documentDetails && response.documentDetails.path) {
      const url = response.documentDetails.path;
      window.open(url, "_blank");
    }
  };

  const handleDocumentPreview = (id: number) => {
    const { documentByIdRequest } = props;
    if (documentByIdRequest) {
      showLoader();
      const payload = {
        values: {
          id: Number(id),
        },
        callback: onDocumentPreviewSuccess,
      };
      documentByIdRequest(payload);
    }
  };

  return (
    <>
      <Box className="content-header">
        <Typography variant="h2" className="heading">
          Documents
        </Typography>
        <Button
          variant="contained"
          className="btn-add"
          onClick={() => handleDocument(false, null)}
          disabled={!pagePermission?.add}
        >
          <img src={plusLightIcon} alt="plus" />
          Add
        </Button>
      </Box>
      <Grid
        container
        spacing={{ xs: "16px", lg: "20px", xl: "24px" }}
        className="content-container"
      >
        <Grid item xs={12}>
          <Card>
            <Box className="table-card-header">
              <Box
                sx={{ justifyContent: "space-between", width: "100%" }}
                className="search-filter-inputs"
              >
                <TextField
                  id="search-box"
                  variant="outlined"
                  className="search-input"
                  placeholder="Search"
                  value={searchText}
                  onChange={handleDocumentSearch}
                  onKeyDown={handleDocumentSearchKeyDown}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        sx={{ position: "absolute", right: "0" }}
                        position="end"
                      >
                        {searchText && (
                          <Tooltip
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            title={CLEAR_TOOLTIP_TITLE}
                            arrow
                          >
                            <IconButton edge="end" onClick={handleClearSearch}>
                              <img src={closeIcon} alt="close" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(event) =>
                    onTextFieldKeyPress(event, searchText?.length)
                  }
                />
              </Box>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={(e) => handleRequestSort(e, "name")}
                      fieldName="Document Name"
                      keyName="name"
                      sxStyle={{
                        maxWidth: "281px",
                        width: "281px",
                        minWidth: "281px",
                      }}
                    />
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={(e) => handleRequestSort(e, "type")}
                      fieldName="Document Type"
                      keyName="type"
                      sxStyle={{ width: "165px", minWidth: "165px" }}
                    />
                    <TableCell
                      align="left"
                      sx={{
                        width: "245px",
                        minWidth: "245px",
                        [projectTheme.breakpoints.down("sm")]: {
                          width: "245px",
                        },
                      }}
                    >
                      Document Link
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        width: "355px",
                        [projectTheme.breakpoints.down("sm")]: {
                          width: "355px",
                        },
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: "100px",
                        [projectTheme.breakpoints.down("sm")]: {
                          width: "112px",
                        },
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentList?.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell
                        sx={{ wordWrap: "break-word" }}
                        component="th"
                        scope="row"
                      >
                        {row.name}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="email">
                          {
                            DOCUMENT_TYPE.find((doc) => doc.value === row.type)
                              ?.label
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="email">
                          {row.link}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="email">
                          {row.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <div className="table-actions">
                          <Tooltip
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            title="Preview"
                            arrow
                          >
                            <IconButton
                              onClick={() => handleDocumentPreview(row.id)}
                              disabled={!pagePermission?.view}
                              className={
                                !pagePermission?.edit ? "disabled-icon" : ""
                              }
                            >
                              <img src={documentPreviewIcon} alt="edit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            title={EDIT_TOOLTIP_TITLE}
                            arrow
                          >
                            <IconButton
                              onClick={() => handleDocument(true, row)}
                              disabled={!pagePermission?.edit}
                              className={
                                !pagePermission?.edit ? "disabled-icon" : ""
                              }
                            >
                              <img src={editIcon} alt="edit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            title={DELETE_TOOLTIP_TITLE}
                            arrow
                          >
                            <IconButton
                              onClick={() =>
                                handleDocumentDeleteModal(Number(row?.id))
                              }
                              disabled={!pagePermission?.delete}
                              className={
                                !pagePermission?.delete ? "disabled-icon" : ""
                              }
                            >
                              <img src={deleteIcon} alt="delete" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {documentList && documentList.length > 0 ? (
              <CustomPagination
                handleChange={handleChange}
                page={page}
                pageNo={pageNo}
                onPageChange={onPageChange}
                listMeta={documentListMeta}
              />
            ) : (
              <NoRecords />
            )}
          </Card>
        </Grid>
      </Grid>

      <AddEditDocument
        isEditDocument={isEditDocument}
        isOpenDocumentModal={isOpenDocumentModal}
        handleDocumentClose={handleDocumentClose}
        handleSaveClick={
          isEditDocument ? handleEditDocument : handleAddDocument
        }
        editDocumentData={editDocumentData}
      />
      <DeleteConfirmationModal
        isOpenDeleteConfirmationModal={isOpenDocumentDeleteModal}
        handleDeleteConfirmationModalClose={handleDocumentDeleteCloseModal}
        deleteConfirmationMessage={DOCUMENT_DELETE_MODAL_MSG}
        handleYesClick={handleDeleteDocument}
      />
    </>
  );
};

export default Documents;
